import { pdf } from "@react-pdf/renderer";
import type { Folder } from "~/api/model";
import logo3xPng from "~/assets/Logo3x.png";
import { downloadBlob } from "~/helpers";
import { getSafeEmailBodyLimit } from "~/utils/browserDetection";
import { formatDateOnly } from "~/utils/date";
import logger from "~/utils/logger";
import { downloadMarkdownAsDocx } from "./markdown-converters";
import { createPdfDocumentFromMarkdown } from "./pdf-markdown-converter";

type PdfCaseInfo = {
  caseName: string;
  dateOfBirth: string;
  dateOfLoss: string;
  claimNumber: string;
};

type PdfDocumentOptions = {
  title: string;
  includeTitle?: boolean;
  titleAlignment?: "left" | "center";
  showHeader?: boolean;
  caseInfo?: PdfCaseInfo;
  includeFooter?: boolean;
  includeLogo?: boolean;
  logoSrc?: string;
  fontFamily?: string;
  lineSpacing?: string;
};

export const downloadSummaryAsDocx = async (
  content: string,
  fileTitle: string,
  title?: string,
  fontFamily?: string,
  lineSpacing?: string
) => {
  const contentWithEmbeddedImages =
    await convertMarkdownImagesToDataUrls(content);
  await downloadMarkdownAsDocx(
    contentWithEmbeddedImages,
    fileTitle,
    title,
    fontFamily,
    lineSpacing
  );
};

/**
 * Convert an image URL to a data URL (base64)
 * This is needed because @react-pdf/renderer doesn't support external URLs with query parameters
 */
export const convertImageToDataUrl = async (url: string): Promise<string> => {
  try {
    // Fetch with credentials to handle S3 presigned URLs
    const response = await fetch(url, {
      mode: "cors",
      credentials: "same-origin",
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch image: ${response.status} ${response.statusText}`
      );
    }

    const blob = await response.blob();
    const contentType = blob.type?.toLowerCase() ?? "";

    logger.info("Image content type:", contentType);

    let isSvgUrl = false;
    try {
      const parsedUrl = new URL(url);
      isSvgUrl = parsedUrl.pathname.toLowerCase().endsWith(".svg");
    } catch {
      isSvgUrl = url.split("?")[0].toLowerCase().endsWith(".svg");
    }

    const isSvg = contentType.includes("image/svg") || isSvgUrl;

    // @react-pdf/renderer doesn't support SVG, so we need to convert it to PNG
    if (isSvg) {
      logger.info("Converting SVG to PNG for PDF compatibility");
      return await convertSvgToPng(blob);
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        logger.info("Successfully converted image to data URL");
        resolve(result);
      };
      reader.onerror = () => {
        logger.error("FileReader error:", reader.error);
        reject(reader.error);
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    logger.error("Failed to convert image to data URL:", error);
    throw error;
  }
};

/**
 * Finds all external (non-data-URL) image references in markdown and converts
 * them to base64 data URLs so they can be embedded in PDF / DOCX exports.
 */
export const convertMarkdownImagesToDataUrls = async (
  markdown: string
): Promise<string> => {
  const IMAGE_RE = /!\[([^\]]*)\]\(([^)]+)\)/g;

  const matches: { fullMatch: string; alt: string; url: string }[] = [];
  let m: RegExpExecArray | null;
  while ((m = IMAGE_RE.exec(markdown)) !== null) {
    const url = m[2];
    if (url.startsWith("data:")) continue;
    matches.push({ fullMatch: m[0], alt: m[1], url });
  }

  if (matches.length === 0) return markdown;

  const conversions = await Promise.allSettled(
    matches.map(async ({ fullMatch, alt, url }) => {
      try {
        const dataUrl = await convertImageToDataUrl(url);
        return { fullMatch, alt, dataUrl };
      } catch (error) {
        logger.error("Failed to convert image URL for export:", url);
        return null;
      }
    })
  );

  let result = markdown;
  for (const conversion of conversions) {
    if (conversion.status === "fulfilled" && conversion.value) {
      const { fullMatch, alt, dataUrl } = conversion.value;
      result = result.replace(fullMatch, () => `![${alt}](${dataUrl})`);
    }
  }

  return result;
};

/**
 * Convert SVG blob to PNG data URL
 * @react-pdf/renderer doesn't support SVG images
 */
export const convertSvgToPng = async (svgBlob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const svgText = event.target?.result as string;

        const img = new Image();
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          throw new Error("Failed to get canvas context");
        }

        const svgBlobForImage = new Blob([svgText], { type: "image/svg+xml" });
        const objectUrl = URL.createObjectURL(svgBlobForImage);

        const cleanup = () => {
          URL.revokeObjectURL(objectUrl);
        };

        img.onload = () => {
          try {
            const width = img.width || 200;
            const height = img.height || 100;

            canvas.width = width * 2;
            canvas.height = height * 2;

            ctx.fillStyle = "white"; // Avoid transparent background artifacts
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

            const pngDataUrl = canvas.toDataURL("image/png");
            logger.info("Successfully converted SVG to PNG");
            resolve(pngDataUrl);
          } catch (error) {
            logger.error("Error while rendering SVG onto canvas:", error);
            reject(error);
          } finally {
            cleanup();
          }
        };

        img.onerror = (error) => {
          logger.error("Failed to load SVG image:", error);
          cleanup();
          reject(new Error("Failed to load SVG image"));
        };

        img.src = objectUrl;
      } catch (error) {
        logger.error("Error in SVG to PNG conversion:", error);
        reject(error);
      }
    };

    reader.onerror = () => {
      logger.error("FileReader error while reading SVG:", reader.error);
      reject(reader.error);
    };

    reader.readAsText(svgBlob);
  });
};

export const generatePDFFile = async (
  content: string,
  title: string,
  options: Partial<Omit<PdfDocumentOptions, "title">> = {}
) => {
  const mergedOptions = {
    includeTitle: true,
    titleAlignment: "center" as const,
    ...options,
  };

  const shouldIncludeLogo = mergedOptions.includeLogo ?? true;

  let logoSrc = shouldIncludeLogo ? logo3xPng : undefined;

  // If a custom logoSrc is provided and it's an external URL, convert it to data URL
  if (
    shouldIncludeLogo &&
    mergedOptions.logoSrc &&
    mergedOptions.logoSrc !== logo3xPng
  ) {
    try {
      // If already a data URL, use it directly
      if (mergedOptions.logoSrc.startsWith("data:")) {
        logoSrc = mergedOptions.logoSrc;
      } else {
        // Convert external URL to base64 data URL
        logger.info(
          "Converting logo URL to data URL in generatePDFFile:",
          mergedOptions.logoSrc
        );
        logoSrc = await convertImageToDataUrl(mergedOptions.logoSrc);
      }
    } catch (error) {
      logger.error(
        "Failed to load custom logo in generatePDFFile, using default:",
        error
      );
      logoSrc = logo3xPng;
    }
  } else if (shouldIncludeLogo && mergedOptions.logoSrc) {
    logoSrc = mergedOptions.logoSrc;
  }

  const pdfDoc = createPdfDocumentFromMarkdown(content, {
    title,
    ...mergedOptions,
    includeLogo: shouldIncludeLogo,
    logoSrc,
  });

  const blob = await pdf(pdfDoc).toBlob();
  return blob;
};

export const downloadSummaryAsPdf = async (
  content: string,
  fileTitle: string,
  title: string,
  caseInfo?: PdfCaseInfo,
  fontFamily?: string,
  lineSpacing?: string,
  logoUrl?: string | null,
  footnote?: string | null
) => {
  // Convert custom logo URL to data URL if provided, otherwise use default
  let logoSrc = logo3xPng;

  if (
    logoUrl &&
    logoUrl !== logo3xPng &&
    logoUrl !== "https://placehold.co/100"
  ) {
    try {
      if (logoUrl.startsWith("data:")) {
        logoSrc = logoUrl;
      } else {
        // Convert external URL to base64 data URL for @react-pdf/renderer
        logger.info("Converting logo URL to data URL:", logoUrl);
        logoSrc = await convertImageToDataUrl(logoUrl);
      }
    } catch (error) {
      logger.error("Failed to load custom logo, using default:", error);
      logoSrc = logo3xPng;
    }
  }

  const contentWithEmbeddedImages =
    await convertMarkdownImagesToDataUrls(content);

  const pdfDoc = createPdfDocumentFromMarkdown(contentWithEmbeddedImages, {
    title,
    includeTitle: true,
    titleAlignment: "left",
    showHeader: Boolean(caseInfo),
    caseInfo,
    includeFooter: true,
    includeLogo: true,
    logoSrc,
    fontFamily,
    lineSpacing,
    footnote,
  });

  const blob = await pdf(pdfDoc).toBlob();
  downloadBlob(blob, `${fileTitle}.pdf`);
};

export const sendSummaryWithEmail = (content: string, title: string) => {
  const subject = encodeURIComponent(title);

  let sanitizedContent = content
    .replace(/[\u2018\u2019]/g, "'") // Replace smart quotes
    .replace(/[\u201C\u201D]/g, '"') // Replace smart double quotes
    .replace(/[\u2013\u2014]/g, "-") // Replace em/en dashes
    .replace(/[\u00B0]/g, " degrees") // Replace degree symbol
    .replace(/[\u2192]/g, "->") // Replace right arrow
    .replace(/[\u2191]/g, "^") // Replace up arrow
    .replace(/[\u2264]/g, "<=") // Replace less than or equal
    .replace(/[^\x00-\x7F]/g, "?"); // Replace remaining non-ASCII characters

  let body = encodeURIComponent(sanitizedContent);

  const maxLength = getSafeEmailBodyLimit();
  if (body.length > maxLength) {
    const truncateAt = Math.floor(maxLength * 0.8); // Leave buffer for encoding
    body = body.substring(0, truncateAt);
    body += encodeURIComponent(
      "\n\n[Content truncated due to length limitations. Please refer to the original document for complete information.]"
    );
  }

  const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
  window.open(mailtoLink, "_blank");
};

export function downloadStringAsFile(content: string, fileTitle: string) {
  const blob = new Blob([content], { type: "text/plain" });
  downloadBlob(blob, `${fileTitle}.txt`);
}

export const getFileTitle = (title: string) => {
  if (title.includes(".")) {
    return title.split(".")[0];
  }
  return title;
};

export function formatCaseInfo(folder: Folder) {
  return {
    caseName: folder.name,
    dateOfBirth: formatDateOnly(folder.dateOfBirth),
    dateOfLoss: formatDateOnly(folder.dateOfLoss),
    claimNumber: folder.claimNumber,
  };
}

/**
 * Removes the Sources section from the content for exports.
 * The Sources section is appended by the AI API and contains clickable page references.
 * This function handles multiple formats:
 * 1. "Sources: [1], [2], [3]..." format (with "Sources:" label and commas)
 * 2. "Sources: 1 2 3" format (with "Sources:" label, space-separated)
 * 3. Inline references embedded directly in text (without "Sources:" label)
 * 4. Raw [source:01,10,11,12,13] markers from AI-generated content
 * 5. Source-map-link anchor tags generated by highlightSourceMapInMarkdown
 * The class attribute may contain additional classes and appear in any position.
 * All page-reference anchor tags should be excluded from exports (plain text, docx, pdf, email).
 */
export function stripSourcesFromContent(content: string): string {
  let result = convertStructuredDataToMarkdown(content);
  result = result.replace(
    /<span\s+class\s*=\s*["']sources-references["']>.*?<\/span>/gs,
    ""
  );
  result = result.replace(
    /<a\s[^>]*class="[^"]*page-reference[^"]*"[^>]*>.*?<\/a>/gs,
    ""
  );
  result = result.replace(
    /<a\s[^>]*class="[^"]*source-map-link[^"]*"[^>]*>.*?<\/a>/gs,
    ""
  );
  result = result.replace(/ *\[source:[\d,\s]+\]/g, "");
  result = result.replace(/ {3,}/g, " ");
  return result.replace(/\n{3,}/g, "\n\n").trim();
}

/**
 * Converts structured data card HTML blocks into readable markdown for exports.
 * These cards are AI-generated HTML within chat messages that display entity information
 * (e.g., persons, dates, medical conditions). The HTML is styled via CSS in the browser
 * but needs to be converted to plain markdown for PDF, DOCX, plain text, and email exports.
 */
export function convertStructuredDataToMarkdown(content: string): string {
  if (!content.includes("structured-data-trailer")) return content;

  let result = content;
  const marker = '<div class="structured-data-trailer">';

  let idx = result.indexOf(marker);
  while (idx !== -1) {
    // Find the matching closing </div> by counting nested divs
    let depth = 0;
    let pos = idx;
    let endPos = -1;

    while (pos < result.length) {
      const openDiv = result.indexOf("<div", pos);
      const closeDiv = result.indexOf("</div>", pos);

      if (closeDiv === -1) break;

      if (openDiv !== -1 && openDiv < closeDiv) {
        depth++;
        pos = openDiv + 4;
      } else {
        depth--;
        if (depth === 0) {
          endPos = closeDiv + "</div>".length;
          break;
        }
        pos = closeDiv + "</div>".length;
      }
    }

    if (endPos === -1) break;

    const trailerHtml = result.substring(idx, endPos);
    const markdown = parseStructuredDataTrailer(trailerHtml);
    result = result.substring(0, idx) + markdown + result.substring(endPos);

    idx = result.indexOf(marker);
  }

  return result;
}

function capitalizeFirst(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function parseStructuredDataTrailer(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const cards = doc.querySelectorAll(".structured-data-card");

  if (cards.length === 0) return "";

  const parts: string[] = [];

  cards.forEach((card) => {
    const type =
      card.querySelector(".structured-data-card__type")?.textContent?.trim() ||
      "";
    const name =
      card.querySelector(".structured-data-card__name")?.textContent?.trim() ||
      "";

    const header = type
      ? `**${capitalizeFirst(type)}: ${capitalizeFirst(name)}**`
      : `**${capitalizeFirst(name)}**`;
    const lines = [header];

    const attrs = card.querySelectorAll(".structured-data-card__attributes li");
    attrs.forEach((li) => {
      const key = li.querySelector(".attr-key")?.textContent?.trim() || "";
      const value = li.querySelector(".attr-value")?.textContent?.trim() || "";
      if (key && value) {
        lines.push(`- **${capitalizeFirst(key)}:** ${capitalizeFirst(value)}`);
      }
    });

    parts.push(lines.join("\n"));
  });

  return "\n\n" + parts.join("\n\n") + "\n";
}

/**
 * Source data from SSE message_end event
 */
export type SourceData = {
  pageId: string;
  documentId: string;
  organizationId: string;
  pageNumber?: number;
  isKnowledgeBase?: boolean;
  batesNumber?: string;
};

/**
 * Formats an array of source data into clickable HTML references.
 * This creates the same HTML structure that was previously included by the backend
 * in WebSocket responses.
 *
 * @param sources - Array of source data from SSE message_end event
 * @returns HTML string with clickable page references wrapped in sources-references span
 */
function escapeHtmlAttr(value: string): string {
  return value
    .replace(/[\r\n]+/g, " ")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export function formatSourcesHtml(sources: SourceData[]): string {
  if (!sources || sources.length === 0) {
    return "";
  }

  const uniqueSources = sources.filter(
    (source, index, self) =>
      index ===
      self.findIndex(
        (s) => s.pageId === source.pageId && s.documentId === source.documentId
      )
  );

  const sourceLinks = uniqueSources.map((source, index) => {
    const attrs = [
      `href="#"`,
      `data-page-id="${escapeHtmlAttr(String(source.isKnowledgeBase && source.pageNumber != null ? source.pageNumber : source.pageId))}"`,
      `data-organization-id="${escapeHtmlAttr(source.organizationId)}"`,
      `data-document-id="${escapeHtmlAttr(source.documentId)}"`,
      source.isKnowledgeBase ? `data-is-knowledge-base="true"` : "",
      source.batesNumber
        ? `data-bates-number="${escapeHtmlAttr(source.batesNumber)}"`
        : "",
      source.batesNumber
        ? `title="Ref. ${escapeHtmlAttr(source.batesNumber)}"`
        : "",
      `class="page-reference inline-flex items-center justify-center rounded-full px-[0.3em] min-w-[1.4em] h-[1.4em] text-[0.7em] font-normal leading-none no-underline transition-colors align-middle relative -top-[0.05em] bg-muted-foreground/20 text-muted-foreground hover:bg-muted-foreground/30 cursor-pointer"`,
    ]
      .filter(Boolean)
      .join(" ");

    return `<a ${attrs}>${index + 1}</a>`;
  });

  return `\n\n<span class="sources-references">Sources: ${sourceLinks.join(" ")}</span>`;
}

/**
 * Regex to match multiple sources in one bracket: [Source 1, Source 3]
 * Captures the inner part: "Source 1, Source 3"
 */
const MULTI_SOURCE_RE = /\[(Source\s+\d+(?:\s*,\s*Source\s+\d+)+)\]/gi;

/**
 * Regex to match multiple full Bates tags in one bracket: [Bates: X, Bates: Y]
 * Captures the inner part: "Bates: X, Bates: Y"
 */
const MULTI_FULL_BATES_RE =
  /\[(Bates:\s*[^\],]+(?:\s*,\s*Bates:\s*[^\],]+)+)\]/gi;

/**
 * Regex to match multiple Bates numbers in one bracket: [Bates: X, Y, Z]
 * Captures the numbers part "X, Y, Z" (supports letters, numbers, dashes)
 */
const MULTI_BATES_NUMBERS_RE =
  /\[Bates:\s*([A-Z0-9-]+(?:\s*,\s*[A-Z0-9-]+)+)\]/gi;

/**
 * Normalizes AI-generated citation markers before HTML conversion.
 * Strips [Summary]/[Snapshot] and converts mixed formats to standard ones.
 * Also splits multiple sources/Bates in one bracket into separate brackets.
 */
export function normalizeCitationMarkers(content: string): string {
  let result = content;

  // Check for multi-source patterns before processing
  const multiSourceMatches = content.match(MULTI_SOURCE_RE);
  if (multiSourceMatches) {
    logger.info("[BATES-CHAIN] Found multi-source citations to split:", {
      matches: multiSourceMatches,
    });
  }

  // Split multiple sources in one bracket: [Source 1, Source 3] -> [Source 1] [Source 3]
  result = result.replace(MULTI_SOURCE_RE, (_match, inner) => {
    const split = inner
      .split(/\s*,\s*/)
      .map((s: string) => `[${s.trim()}]`)
      .join(" ");
    logger.info("[BATES-CHAIN] Split multi-source citation:", {
      original: _match,
      result: split,
    });
    return split;
  });

  // Split multiple full Bates tags: [Bates: X, Bates: Y] -> [Bates: X] [Bates: Y]
  result = result.replace(MULTI_FULL_BATES_RE, (_match, inner) => {
    const split = inner
      .split(/\s*,\s*(?=Bates:)/i)
      .map((s: string) => `[${s.trim()}]`)
      .join(" ");
    logger.info("[BATES-CHAIN] Split multi-Bates citation:", {
      original: _match,
      result: split,
    });
    return split;
  });

  // Split multiple Bates numbers: [Bates: X, Y, Z] -> [Bates: X] [Bates: Y] [Bates: Z]
  result = result.replace(MULTI_BATES_NUMBERS_RE, (_match, nums) => {
    const split = nums
      .split(/\s*,\s*/)
      .map((n: string) => `[Bates: ${n.trim()}]`)
      .join(" ");
    logger.info("[BATES-CHAIN] Split multi-Bates-numbers citation:", {
      original: _match,
      result: split,
    });
    return split;
  });

  result = result.replace(
    /\[(?:Summary|Snapshot),\s*(Source\s+\d+)\]/gi,
    "[$1]"
  );
  result = result.replace(
    /\[(Source\s+\d+),\s*(?:Summary|Snapshot)\]/gi,
    "[$1]"
  );
  result = result.replace(
    /\[(?:Summary|Snapshot),\s*(Bates:\s*[^\]]+)\]/gi,
    "[$1]"
  );
  result = result.replace(
    /\[(Bates:\s*[^\],]+),\s*(?:Summary|Snapshot)\]/gi,
    "[$1]"
  );
  result = result.replace(/\[(?:Summary|Snapshot)\]/gi, "");
  return result;
}
